/**
 * Step 4 — Confirm & Download.
 *
 * Shows "Ready to generate X EDDs for Y work orders (Z files total)", runs the flow (one click), then
 * offers "Download All" (a zip with a folder per work order, files per EDD) and "Email Results" (the
 * EmailModal). After an action, a "Generation complete" summary appears with re-download links.
 *
 * Inline edits made in the Preview step are intentionally NOT pushed into the run: the engine output is
 * deterministic and byte-frozen by the test suite. Inline edits are a "spot-check & note" affordance for
 * this preview; to change generated values, adjust the config's fields or a cross-table mapping (the
 * preview surfaces exactly which column/source to fix).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  downloadFlowBundle,
  useRunFlow,
  type FlowComboPreview,
  type FlowRunOut,
} from "../../api/generateFlow";
import { errorMessage } from "../../api/client";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { Spinner } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { EmailModal } from "./EmailModal";
import { track, EVENTS } from "./telemetry";
import type { WizardState } from "./types";

export function ConfirmStep({
  state,
  combos,
  job,
  onGenerated,
  onStartOver,
}: {
  state: WizardState;
  combos: FlowComboPreview[];
  job: FlowRunOut | null;
  onGenerated: (job: FlowRunOut) => void;
  onStartOver: () => void;
}) {
  const toast = useToast();
  const run = useRunFlow();
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const workOrderCount = state.workOrders.length;
  const formatCount = state.formats.length;
  const eddCount = workOrderCount * formatCount;
  const previewFileEstimate = combos.length * Math.max(state.outputFormats.length, 1);

  const generate = async () => {
    try {
      const result = await run.mutateAsync({
        name: state.runName.trim() || undefined,
        sources: state.workOrders.map((w) => w.source),
        formats: state.formats.map((f) => f.selector),
        output_formats: state.outputFormats,
        validate: true,
      });
      track(EVENTS.edd_generated, {
        format_names: state.formats.map((f) => f.name),
        work_order_count: workOrderCount,
        output_format: state.outputFormats.join(","),
      });
      onGenerated(result);
      if (result.status === "completed") toast.success("All deliverables generated.");
      else if (result.status === "partial") toast.info("Some deliverables failed — check the summary.");
      else toast.error("Generation failed.");
    } catch (err) {
      track(EVENTS.error_encountered, { page: "generate_flow_confirm", error_type: "run_failed" });
      toast.error(errorMessage(err));
    }
  };

  const downloadAll = async () => {
    if (!job) return;
    setDownloading(true);
    try {
      await downloadFlowBundle(job.id, job.name || "edd-deliverables");
      track(EVENTS.edd_downloaded, { delivery_method: "download", format_count: formatCount });
    } catch (err) {
      track(EVENTS.error_encountered, { page: "generate_flow_download", error_type: "download_failed" });
      toast.error(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  // ── after a run: "Generation complete" summary + re-download ──
  if (job) {
    const workOrderLabel =
      state.workOrders.length === 1 ? state.workOrders[0].label : `${state.workOrders.length} work orders`;
    return (
      <div className="stack">
        <div className="row" style={{ gap: "var(--space-2)", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Generation complete</h2>
          <Badge
            tone={job.status === "completed" ? "success" : job.status === "partial" ? "warning" : "danger"}
          >
            {job.status}
          </Badge>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Generated {job.completed_items} of {job.total_items} EDD{job.total_items === 1 ? "" : "s"} (
          {job.file_count} file{job.file_count === 1 ? "" : "s"}) across {job.source_count} work order
          {job.source_count === 1 ? "" : "s"}.
          {job.failed_items > 0 ? ` ${job.failed_items} failed.` : ""}
        </p>

        {job.warnings.slice(0, 4).map((w, i) => (
          <div key={i} className="banner banner--warning">
            {w}
          </div>
        ))}

        <div className="row row--wrap" style={{ gap: "var(--space-2)" }}>
          <Button variant="primary" loading={downloading} disabled={!job.has_result} onClick={downloadAll}>
            Download all
          </Button>
          <Button variant="default" disabled={!job.has_result} onClick={() => setEmailOpen(true)}>
            Email results
          </Button>
          <Link className="btn btn--ghost" to={`/generate/jobs/${job.id}`}>
            Open in history
          </Link>
          {/* Wipe the wizard and return to step 1 — "start a brand-new EDD from scratch". */}
          <Button variant="ghost" onClick={onStartOver}>
            Generate a new EDD
          </Button>
        </div>

        <EmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          jobId={job.id}
          workOrderLabel={workOrderLabel}
          onSent={() => undefined}
        />
      </div>
    );
  }

  // ── before a run: confirmation summary + Generate ──
  return (
    <div className="stack">
      <div>
        <h2 style={{ marginBottom: 4 }}>Confirm &amp; generate</h2>
        <p className="muted" style={{ margin: 0 }}>
          Ready to generate <strong>{eddCount}</strong> EDD{eddCount === 1 ? "" : "s"} for{" "}
          <strong>{workOrderCount}</strong> work order{workOrderCount === 1 ? "" : "s"} (
          {previewFileEstimate} file{previewFileEstimate === 1 ? "" : "s"} total).
        </p>
      </div>

      <div className="split-cols">
        <div className="card card--pad-sm stack" style={{ gap: "var(--space-1)" }}>
          <strong>Work orders ({workOrderCount})</strong>
          {state.workOrders.map((w) => (
            <span key={w.id} className="faint">
              {w.label}
            </span>
          ))}
        </div>
        <div className="card card--pad-sm stack" style={{ gap: "var(--space-1)" }}>
          <strong>Formats ({formatCount})</strong>
          {state.formats.map((f) => (
            <span key={f.id} className="faint">
              {f.name}
            </span>
          ))}
          <span className="faint">Output: {state.outputFormats.join(", ") || "each format's default"}</span>
        </div>
      </div>

      {combos.some((c) => c.status === "errors") && (
        <div className="banner banner--warning">
          Some previews have errors. You can still generate, but the receiving system may reject those
          files — review the Preview step first.
        </div>
      )}

      {run.isPending ? (
        <div className="center-pad">
          <Spinner size="lg" />
          <span>Generating {eddCount} deliverable{eddCount === 1 ? "" : "s"}…</span>
        </div>
      ) : (
        <Button variant="primary" disabled={eddCount === 0} onClick={generate}>
          Generate {eddCount} EDD{eddCount === 1 ? "" : "s"}
        </Button>
      )}
    </div>
  );
}
